import { useEffect, useState } from "react";
import {
  Button, Checkbox, Form, Input, InputNumber, Modal,
  Select, Space, Switch, Tag, Typography, message,
} from "antd";
import {
  AlertCircle, CheckCircle,
  Clock, Edit2, Home, KeyRound,
  Mail, Phone, Plus,
  TrendingUp, Trash2, Users, X,
} from "lucide-react";
import axiosClient from "../../api/axiosClient";
import "./AdminBoothRentalPage.css";

const { Text } = Typography;

type BillingCycle = "weekly" | "bi-weekly" | "monthly";
type BoothStatus = "active" | "inactive" | "overdue";
type PaymentStatus = "paid" | "pending" | "overdue";

interface BoothPayment {
  payment_id: string;
  booth_id: string;
  amount: number;
  method: string;
  status: PaymentStatus;
  paid_at: string;
}

interface BoothRenter {
  booth_id: string;
  booth_number: string;
  renter_name: string;
  phone: string;
  email: string;
  specialty: string;
  rental_rate: number;
  billing_cycle: BillingCycle;
  lease_start: string | null;
  lease_end: string | null;
  next_payment_due: string | null;
  security_deposit: number;
  status: BoothStatus;
  user_id: string | null;
  autopay_enabled: boolean;
  renter_routing_number: string;
  renter_account_number: string;
  salon_routing_number: string;
  salon_account_number: string;
  collects_payments: boolean;
  card_processor: string | null;
  payment_history: BoothPayment[];
}

const AVATAR_COLORS = ["#ec4899", "#14b8a6", "#a855f7", "#f59e0b", "#3b82f6"];

function avatarColor(id: string): string {
  const sum = id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function initials(name: string): string {
  return name.split(" ").map((n) => n[0] ?? "").join("").slice(0, 2).toUpperCase();
}

function cycleLabel(cycle: BillingCycle): string {
  return cycle === "weekly" ? "/wk" : cycle === "bi-weekly" ? "/2wks" : "/mo";
}

function estimatedMonthly(rate: number, cycle: BillingCycle): number {
  if (cycle === "weekly") return rate * 4.33;
  if (cycle === "bi-weekly") return rate * 2.17;
  return rate;
}

function formatDate(d: string | null): string {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return d;
  }
}

// ── Add/Edit Modal ────────────────────────────────────────────────────────────

function BoothModal({
  booth,
  onClose,
  onSave,
}: {
  booth?: BoothRenter;
  onClose: () => void;
  onSave: (values: Omit<BoothRenter, "booth_id" | "payment_history" | "user_id"> & { give_portal_access: boolean }) => void;
}) {
  const [form] = Form.useForm();
  const [autopayEnabled, setAutopayEnabled] = useState(booth?.autopay_enabled ?? false);
  const [collectsPayments, setCollectsPayments] = useState(booth?.collects_payments ?? true);
  const [showRenterAcct, setShowRenterAcct] = useState(false);
  const [showSalonAcct, setShowSalonAcct] = useState(false);

  async function handleOk() {
    const vals = await form.validateFields();
    onSave({
      booth_number: vals.booth_number,
      renter_name: vals.renter_name ?? "",
      phone: vals.phone ?? "",
      email: vals.email ?? "",
      specialty: vals.specialty ?? "",
      rental_rate: vals.rental_rate ?? 0,
      billing_cycle: vals.billing_cycle,
      lease_start: vals.lease_start ?? null,
      lease_end: vals.lease_end ?? null,
      next_payment_due: vals.next_payment_due ?? null,
      security_deposit: vals.security_deposit ?? 0,
      status: vals.status,
      collects_payments: collectsPayments,
      card_processor: booth?.card_processor ?? null,
      autopay_enabled: autopayEnabled,
      renter_routing_number: autopayEnabled ? vals.renter_routing_number ?? "" : "",
      renter_account_number: autopayEnabled ? vals.renter_account_number ?? "" : "",
      salon_routing_number: autopayEnabled ? vals.salon_routing_number ?? "" : "",
      salon_account_number: autopayEnabled ? vals.salon_account_number ?? "" : "",
      give_portal_access: vals.give_portal_access ?? false,
    });
  }

  const initialValues = booth
    ? {
        ...booth,
        lease_start: booth.lease_start ?? undefined,
        lease_end: booth.lease_end ?? undefined,
        next_payment_due: booth.next_payment_due ?? undefined,
      }
    : { status: "active", billing_cycle: "weekly" };

  return (
    <Modal
      open
      title={booth ? `Edit — ${booth.booth_number}` : "Add New Booth / Renter"}
      onCancel={onClose}
      onOk={handleOk}
      okText={booth ? "Save Changes" : "Add Booth"}
      width={560}
    >
      <Form form={form} layout="vertical" initialValues={initialValues} style={{ marginTop: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="booth_number" label="Booth / Chair Number" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Booth 3" />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Available / Inactive" },
                { value: "overdue", label: "Overdue" },
              ]}
            />
          </Form.Item>
        </div>

        <Form.Item name="renter_name" label="Renter Full Name">
          <Input placeholder="Full Name" />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="(000) 000-0000" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" placeholder="email@example.com" />
          </Form.Item>
        </div>

        <Form.Item name="specialty" label="Specialty / Service Type">
          <Input placeholder="e.g. Barber, Color Specialist, Nail Tech" />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="rental_rate" label="Rental Rate ($)">
            <InputNumber style={{ width: "100%" }} min={0} precision={2} prefix="$" />
          </Form.Item>
          <Form.Item name="billing_cycle" label="Billing Cycle">
            <Select
              options={[
                { value: "weekly", label: "Weekly" },
                { value: "bi-weekly", label: "Bi-Weekly" },
                { value: "monthly", label: "Monthly" },
              ]}
            />
          </Form.Item>
        </div>

        <Form.Item name="security_deposit" label="Security Deposit ($)">
          <InputNumber style={{ width: "100%" }} min={0} precision={2} prefix="$" />
        </Form.Item>

        <div style={{
          borderRadius: 10, border: `1px solid ${collectsPayments ? "#ddd6fe" : "#e5e7eb"}`,
          background: collectsPayments ? "#f5f3ff" : "#fafafa", padding: 12, marginBottom: 16,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <Text style={{ fontWeight: 600, fontSize: 13 }}>Collects Payments</Text>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {collectsPayments
                  ? "Renter collects client payments at the chair"
                  : "Salon collects — funds distributed via Payroll"}
              </div>
            </div>
            <Switch checked={collectsPayments} onChange={setCollectsPayments} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="lease_start" label="Lease Start">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="lease_end" label="Lease End">
            <Input type="date" />
          </Form.Item>
        </div>

        <Form.Item name="next_payment_due" label="Next Payment Due">
          <Input type="date" />
        </Form.Item>

        {/* Portal access */}
        {booth?.user_id ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f0fdf4", borderRadius: 8, marginBottom: 16 }}>
            <KeyRound size={14} color="#16a34a" />
            <Text style={{ fontSize: 13, color: "#16a34a" }}>This renter already has portal access.</Text>
          </div>
        ) : (
          <Form.Item name="give_portal_access" valuePropName="checked" style={{ marginBottom: 16 }}>
            <Checkbox>
              Give portal access <Text type="secondary" style={{ fontWeight: 400 }}>— creates a login so they can see their own calendar and "My Booth Clients"</Text>
            </Checkbox>
          </Form.Item>
        )}

        {/* Autopay */}
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Autopay</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Automatically collect rent via ACH on the billing cycle above.</div>
            </div>
            <Switch checked={autopayEnabled} onChange={setAutopayEnabled} />
          </div>

          {autopayEnabled && (
            <div style={{ marginTop: 14 }}>
              <Text strong style={{ fontSize: 12 }}>Renter's Bank Account</Text>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 6, marginBottom: 12 }}>
                <Form.Item name="renter_routing_number" label="Routing Number" style={{ marginBottom: 0 }}>
                  <Input placeholder="Routing number" />
                </Form.Item>
                <Form.Item name="renter_account_number" label="Account Number" style={{ marginBottom: 0 }}>
                  <Input.Password
                    placeholder="Account number"
                    visibilityToggle={{ visible: showRenterAcct, onVisibleChange: setShowRenterAcct }}
                  />
                </Form.Item>
              </div>
              <Text strong style={{ fontSize: 12 }}>Salon's Bank Account</Text>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 6 }}>
                <Form.Item name="salon_routing_number" label="Routing Number" style={{ marginBottom: 0 }}>
                  <Input placeholder="Routing number" />
                </Form.Item>
                <Form.Item name="salon_account_number" label="Account Number" style={{ marginBottom: 0 }}>
                  <Input.Password
                    placeholder="Account number"
                    visibilityToggle={{ visible: showSalonAcct, onVisibleChange: setShowSalonAcct }}
                  />
                </Form.Item>
              </div>
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
}

// ── Record Payment Modal ──────────────────────────────────────────────────────

function RecordPaymentModal({
  booth,
  onClose,
  onSave,
}: {
  booth: BoothRenter;
  onClose: () => void;
  onSave: (amount: number, method: string, status: PaymentStatus) => void;
}) {
  const [form] = Form.useForm();

  async function handleOk() {
    const vals = await form.validateFields();
    onSave(vals.amount, vals.method, vals.status);
  }

  return (
    <Modal
      open
      title={`Record Payment — ${booth.booth_number}`}
      onCancel={onClose}
      onOk={handleOk}
      okText="Record Payment"
      width={400}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          amount: booth.rental_rate,
          method: "Direct Deposit",
          status: "paid",
        }}
        style={{ marginTop: 16 }}
      >
        <Form.Item name="amount" label="Amount ($)" rules={[{ required: true }]}>
          <InputNumber style={{ width: "100%" }} min={0.01} precision={2} prefix="$" />
        </Form.Item>
        <Form.Item name="method" label="Payment Method" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "Direct Deposit", label: "Direct Deposit" },
              { value: "Cash", label: "Cash" },
              { value: "Check", label: "Check" },
              { value: "Card", label: "Card" },
            ]}
          />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "paid", label: "Paid" },
              { value: "pending", label: "Pending" },
              { value: "overdue", label: "Overdue" },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: BoothStatus }) {
  if (status === "active") return <Tag color="success">Active</Tag>;
  if (status === "overdue") return <Tag color="error">Overdue</Tag>;
  return <Tag color="default">Available</Tag>;
}

function PayStatusBadge({ status }: { status: PaymentStatus }) {
  if (status === "paid") return (
    <span className="br-pay-badge br-pay-paid"><CheckCircle size={11} /> Paid</span>
  );
  if (status === "overdue") return (
    <span className="br-pay-badge br-pay-overdue"><AlertCircle size={11} /> Overdue</span>
  );
  return <span className="br-pay-badge br-pay-pending"><Clock size={11} /> Pending</span>;
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminBoothRentalPage() {
  const [booths, setBooths] = useState<BoothRenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<BoothRenter | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editBooth, setEditBooth] = useState<BoothRenter | null>(null);
  const [paymentBooth, setPaymentBooth] = useState<BoothRenter | null>(null);
  const [tempPasswordInfo, setTempPasswordInfo] = useState<{ name: string; password: string } | null>(null);

  useEffect(() => {
    fetchBooths();
  }, []);

  async function fetchBooths() {
    setLoading(true);
    try {
      const resp = await axiosClient.get("/booth-renters");
      const data: BoothRenter[] = resp.data ?? [];
      setBooths(data);
      // Keep selected in sync after refresh
      setSelected((prev) => (prev ? (data.find((b) => b.booth_id === prev.booth_id) ?? null) : null));
    } catch {
      message.error("Failed to load booth renters");
    } finally {
      setLoading(false);
    }
  }

  // Stats
  const activeCount = booths.filter((b) => b.status === "active").length;
  const overdueCount = booths.filter((b) => b.status === "overdue").length;
  const monthlyRevenue = booths
    .filter((b) => b.status !== "inactive")
    .reduce((s, b) => s + estimatedMonthly(b.rental_rate, b.billing_cycle), 0);

  async function handleAdd(data: Omit<BoothRenter, "booth_id" | "payment_history" | "user_id"> & { give_portal_access: boolean }) {
    try {
      const resp = await axiosClient.post("/booth-renters", data);
      await fetchBooths();
      // Select the newly created booth
      const updated = await axiosClient.get("/booth-renters");
      const all: BoothRenter[] = updated.data ?? [];
      const created = all.find((b) => b.booth_id === resp.data.booth_id);
      if (created) setSelected(created);
      setAddOpen(false);
      message.success("Booth added");
      if (resp.data.temp_password) {
        setTempPasswordInfo({ name: data.renter_name || data.booth_number, password: resp.data.temp_password });
      }
    } catch (err: any) {
      message.error(err?.response?.data ?? "Failed to add booth");
    }
  }

  async function handleEdit(data: Omit<BoothRenter, "booth_id" | "payment_history" | "user_id"> & { give_portal_access: boolean }) {
    if (!editBooth) return;
    try {
      const resp = await axiosClient.put(`/booth-renters/${editBooth.booth_id}`, data);
      await fetchBooths();
      setEditBooth(null);
      message.success("Booth updated");
      if (resp.data?.temp_password) {
        setTempPasswordInfo({ name: data.renter_name || data.booth_number, password: resp.data.temp_password });
      }
    } catch (err: any) {
      message.error(err?.response?.data ?? "Failed to update booth");
    }
  }

  async function handleDelete(booth: BoothRenter) {
    try {
      await axiosClient.delete(`/booth-renters/${booth.booth_id}`);
      if (selected?.booth_id === booth.booth_id) setSelected(null);
      await fetchBooths();
      message.success("Renter removed");
    } catch {
      message.error("Failed to remove renter");
    }
  }

  async function handleRecordPayment(amount: number, method: string, status: PaymentStatus) {
    if (!paymentBooth) return;
    try {
      await axiosClient.post(`/booth-renters/${paymentBooth.booth_id}/payments`, { amount, method, status });
      await fetchBooths();
      setPaymentBooth(null);
      message.success("Payment recorded");
    } catch {
      message.error("Failed to record payment");
    }
  }

  return (
    <div className="admin-br-page">
      {/* Page header */}
      <div className="br-page-header">
        <div>
          <h1 className="br-page-title">Booth Rental</h1>
          <p className="br-page-sub">Manage booth renters, lease agreements, and payment tracking</p>
        </div>
        <Button type="primary" icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>
          Add Renter
        </Button>
      </div>

      {/* Stats */}
      <div className="br-stats-grid">
        <div className="br-stat-card">
          <div className="br-stat-icon br-icon-blue"><Home size={20} /></div>
          <div>
            <div className="br-stat-label">Total Booths</div>
            <div className="br-stat-value">{booths.length}</div>
          </div>
        </div>
        <div className="br-stat-card">
          <div className="br-stat-icon br-icon-green"><Users size={20} /></div>
          <div>
            <div className="br-stat-label">Occupied</div>
            <div className="br-stat-value">{activeCount}</div>
          </div>
        </div>
        <div className="br-stat-card">
          <div className="br-stat-icon br-icon-purple"><TrendingUp size={20} /></div>
          <div>
            <div className="br-stat-label">Est. Monthly Revenue</div>
            <div className="br-stat-value">${monthlyRevenue.toFixed(0)}</div>
          </div>
        </div>
        <div className="br-stat-card">
          <div className={`br-stat-icon ${overdueCount > 0 ? "br-icon-red" : "br-icon-gray"}`}><AlertCircle size={20} /></div>
          <div>
            <div className="br-stat-label">Overdue Payments</div>
            <div className={`br-stat-value ${overdueCount > 0 ? "br-overdue-val" : ""}`}>{overdueCount}</div>
          </div>
        </div>
      </div>

      {/* Main layout: sidebar + detail */}
      <div className="br-main-layout">
        {/* Booth list sidebar */}
        <div className="br-sidebar">
          <div className="br-sidebar-header">Booths &amp; Renters</div>
          <div className="br-sidebar-list">
            {loading ? (
              <div className="br-sidebar-empty">Loading…</div>
            ) : booths.length === 0 ? (
              <div className="br-sidebar-empty">No booths yet</div>
            ) : (
              booths.map((booth) => (
                <button
                  key={booth.booth_id}
                  className={`br-sidebar-item${selected?.booth_id === booth.booth_id ? " br-sidebar-item-active" : ""}`}
                  onClick={() => setSelected(booth)}
                >
                  {booth.status === "inactive" ? (
                    <div className="br-avatar br-avatar-empty"><Home size={18} /></div>
                  ) : (
                    <div className="br-avatar" style={{ background: avatarColor(booth.booth_id) }}>
                      {initials(booth.renter_name)}
                    </div>
                  )}
                  <div className="br-sidebar-item-info">
                    <div className="br-sidebar-item-row">
                      <span className="br-sidebar-booth-num">{booth.booth_number}</span>
                      <StatusBadge status={booth.status} />
                    </div>
                    <div className="br-sidebar-renter">
                      {booth.status === "inactive" ? "Available" : booth.renter_name}
                    </div>
                    {booth.status !== "inactive" && (
                      <div className="br-sidebar-rate">
                        ${booth.rental_rate}{cycleLabel(booth.billing_cycle)}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div className="br-detail">
          {!selected ? (
            <div className="br-empty-state">
              <div className="br-empty-icon">
                <svg width="44" height="44" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: "#3b82f6" }}>
                  <rect x="5" y="13" width="18" height="5" rx="2" fill="currentColor" opacity="0.85" />
                  <rect x="8" y="4" width="12" height="10" rx="3" fill="currentColor" />
                  <rect x="10" y="2" width="8" height="4" rx="2" fill="currentColor" opacity="0.7" />
                  <rect x="4" y="13" width="3" height="4" rx="1" fill="currentColor" opacity="0.6" />
                  <rect x="21" y="13" width="3" height="4" rx="1" fill="currentColor" opacity="0.6" />
                  <rect x="12" y="18" width="4" height="5" rx="1" fill="currentColor" opacity="0.7" />
                  <rect x="7" y="23" width="14" height="2.5" rx="1.25" fill="currentColor" opacity="0.5" />
                </svg>
              </div>
              <h3 className="br-empty-title">Select a Booth</h3>
              <p className="br-empty-sub">Click a booth from the list to view details and payment history.</p>
            </div>
          ) : selected.status === "inactive" ? (
            <div className="br-available-state">
              <div className="br-available-icon"><Home size={28} color="#9ca3af" /></div>
              <h3 className="br-available-title">{selected.booth_number}</h3>
              <p className="br-available-sub">This booth is currently available for rent.</p>
              <Button type="primary" onClick={() => setEditBooth(selected)}>Add Renter</Button>
            </div>
          ) : (
            <div className="br-detail-content">
              {/* Renter profile card */}
              <div className="br-card">
                <div className="br-profile-row">
                  <div className="br-profile-left">
                    <div
                      className="br-avatar-lg"
                      style={{ background: avatarColor(selected.booth_id) }}
                    >
                      {initials(selected.renter_name)}
                    </div>
                    <div>
                      <h3 className="br-renter-name">{selected.renter_name}</h3>
                      <div className="br-renter-meta">
                        <span>{selected.specialty}</span>
                        <span className="br-dot">·</span>
                        <StatusBadge status={selected.status} />
                        <span className="br-dot">·</span>
                        <Tag color={selected.collects_payments ? "purple" : "default"} style={{ marginInlineStart: 0 }}>
                          {selected.collects_payments ? "Collects Own Payments" : "Salon Collects"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  <Space>
                    <Button
                      icon={<Edit2 size={14} />}
                      onClick={() => setEditBooth(selected)}
                    >
                      Edit
                    </Button>
                    <Button
                      danger
                      icon={<Trash2 size={14} />}
                      onClick={() => handleDelete(selected)}
                    >
                      Remove
                    </Button>
                  </Space>
                </div>

                <div className="br-contact-row">
                  <span className="br-contact-item"><Phone size={14} color="#9ca3af" /> {selected.phone || "—"}</span>
                  <span className="br-contact-item"><Mail size={14} color="#9ca3af" /> {selected.email || "—"}</span>
                  <span className="br-contact-item"><Home size={14} color="#9ca3af" /> {selected.booth_number}</span>
                </div>
              </div>

              {/* Lease & Rate */}
              <div className="br-card">
                <h4 className="br-card-title">Lease Agreement</h4>
                <div className="br-lease-grid">
                  <div>
                    <div className="br-lease-label">Rental Rate</div>
                    <div className="br-lease-value-lg">
                      ${selected.rental_rate}
                      <span className="br-lease-cycle">{cycleLabel(selected.billing_cycle)}</span>
                    </div>
                    <div className="br-lease-sub">{selected.billing_cycle} billing</div>
                  </div>
                  <div>
                    <div className="br-lease-label">Security Deposit</div>
                    <div className="br-lease-value-lg">${selected.security_deposit}</div>
                    <div className="br-lease-sub" style={{ color: "#16a34a" }}>On file</div>
                  </div>
                  <div>
                    <div className="br-lease-label">Lease Start</div>
                    <div className="br-lease-value">{formatDate(selected.lease_start)}</div>
                  </div>
                  <div>
                    <div className="br-lease-label">Lease End</div>
                    <div className="br-lease-value">{formatDate(selected.lease_end)}</div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="br-lease-label">Next Payment Due</div>
                    <div className={`br-lease-value ${selected.status === "overdue" ? "br-overdue-text" : ""}`}>
                      {selected.status === "overdue" && <AlertCircle size={14} color="#dc2626" style={{ marginRight: 6 }} />}
                      {formatDate(selected.next_payment_due)}
                      {selected.status === "overdue" && (
                        <span className="br-overdue-chip">OVERDUE</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment history */}
              <div className="br-card br-card-no-pad">
                <div className="br-pay-header">
                  <h4 className="br-card-title">Payment History</h4>
                  <Button
                    type="link"
                    icon={<Plus size={14} />}
                    onClick={() => setPaymentBooth(selected)}
                  >
                    Record Payment
                  </Button>
                </div>
                {selected.payment_history.length === 0 ? (
                  <div className="br-pay-empty">No payment history yet.</div>
                ) : (
                  <table className="br-pay-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th className="text-right">Amount</th>
                        <th className="text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.payment_history.map((pay) => (
                        <tr key={pay.payment_id}>
                          <td>{new Date(pay.paid_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                          <td><Text type="secondary">{pay.method}</Text></td>
                          <td className="text-right"><strong>${pay.amount.toFixed(2)}</strong></td>
                          <td className="text-right"><PayStatusBadge status={pay.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {addOpen && (
        <BoothModal onClose={() => setAddOpen(false)} onSave={handleAdd} />
      )}
      {editBooth && (
        <BoothModal booth={editBooth} onClose={() => setEditBooth(null)} onSave={handleEdit} />
      )}
      {paymentBooth && (
        <RecordPaymentModal
          booth={paymentBooth}
          onClose={() => setPaymentBooth(null)}
          onSave={handleRecordPayment}
        />
      )}

      <Modal
        title="Portal Access Granted"
        open={!!tempPasswordInfo}
        onOk={() => setTempPasswordInfo(null)}
        onCancel={() => setTempPasswordInfo(null)}
        okText="Done"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <p>Share this temporary password with <strong>{tempPasswordInfo?.name}</strong> — they should change it after logging in.</p>
        <Tag style={{ fontSize: 16, padding: "6px 12px", fontFamily: "monospace" }}>{tempPasswordInfo?.password}</Tag>
      </Modal>
    </div>
  );
}
